/**
 * F&B 입점 업체 등록/수정 폼 페이지
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { fnbService } from '../../services/fnbService';
import { FnbVendor } from '../../types/fnb';

interface FnbVendorFormData {
  vendor_name: string;
  vendor_code: string;
  vendor_type?: string;
  location?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  commission_rate: number;
  contract_start?: string;
  contract_end?: string;
  status: 'active' | 'inactive';
}

const FnbVendorForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vendorId = id && id !== 'new' ? parseInt(id, 10) : 0;
  const isEdit = !!vendorId && vendorId > 0;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FnbVendorFormData>({
    defaultValues: {
      status: 'active',
      commission_rate: 0,
    },
  });

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['fnbVendor', vendorId],
    queryFn: () => fnbService.getVendor(vendorId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (vendor && isEdit) {
      reset({
        vendor_name: vendor.vendor_name,
        vendor_code: vendor.vendor_code,
        vendor_type: vendor.vendor_type || '',
        location: vendor.location || '',
        contact_name: vendor.contact_name || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        commission_rate: vendor.commission_rate,
        contract_start: vendor.contract_start ? vendor.contract_start.split('T')[0] : '',
        contract_end: vendor.contract_end ? vendor.contract_end.split('T')[0] : '',
        status: vendor.status,
      });
    }
  }, [vendor, isEdit, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<FnbVendor>) => fnbService.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fnbVendors'] });
      navigate('/admin/fnb/vendors');
    },
    onError: (error: any) => {
      alert(`등록 실패: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<FnbVendor>) => fnbService.updateVendor(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fnbVendors'] });
      queryClient.invalidateQueries({ queryKey: ['fnbVendor', vendorId] });
      navigate(`/admin/fnb/vendors/${vendorId}`);
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const onSubmit = (data: FnbVendorFormData) => {
    const formData: Partial<FnbVendor> = {
      ...data,
      commission_rate: Number(data.commission_rate),
    };

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isEdit ? 'F&B 입점 업체 수정' : 'F&B 입점 업체 등록'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/fnb/vendors')}>
            목록으로
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>업체명 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('vendor_name', { required: '업체명을 입력해주세요.' })}
                      isInvalid={!!errors.vendor_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vendor_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>업체 코드 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('vendor_code', { required: '업체 코드를 입력해주세요.' })}
                      isInvalid={!!errors.vendor_code}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vendor_code?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>업체 유형</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('vendor_type')}
                      placeholder="예: 카페, 레스토랑, 푸드코트"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>위치</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('location')}
                      placeholder="설치 위치"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>담당자</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('contact_name')}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>이메일</Form.Label>
                    <Form.Control
                      type="email"
                      {...register('email')}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>연락처</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('phone')}
                      placeholder="010-1234-5678"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>수수료율 (%) <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('commission_rate', { 
                        required: '수수료율을 입력해주세요.',
                        min: { value: 0, message: '0 이상의 값을 입력해주세요.' },
                        max: { value: 100, message: '100 이하의 값을 입력해주세요.' },
                      })}
                      isInvalid={!!errors.commission_rate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.commission_rate?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>계약 시작일</Form.Label>
                    <Form.Control
                      type="date"
                      {...register('contract_start')}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>계약 종료일</Form.Label>
                    <Form.Control
                      type="date"
                      {...register('contract_end')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>상태 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Select
                      {...register('status', { required: '상태를 선택해주세요.' })}
                      isInvalid={!!errors.status}
                    >
                      <option value="active">활성</option>
                      <option value="inactive">비활성</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => navigate('/admin/fnb/vendors')}>
                  취소
                </Button>
                <Button variant="primary" type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? '수정 중...' : '등록 중...'}
                    </>
                  ) : (
                    isEdit ? '수정' : '등록'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FnbVendorForm;

